import { z } from "zod";
import { calculationItemInputSchema } from "./calculation.js";
import { LOT_MAX_DATA_ROWS, lotMappingSchema, lotRouteSchema, researchMissingSchema, } from "./lot-mapping.js";
import { LOT_MAX_REPAIRS, lotRepairSchema } from "./lot-repair.js";
import { isCanonicalBase64 } from "./nfe-observations.js";
import { workbookIssueSchema } from "./workbook.js";
/**
 * Largest calculator file (xlsx, xlsm, csv or delimited txt) the API reads. A file travels either inline as
 * base64 JSON (`contentBase64`, up to LOT_INLINE_UPLOAD_MAX_BYTES: the route body limit, far
 * below the 32 MB request cap of API Gateway and Cloud Run) or, when larger, straight to Cloud
 * Storage through an upload session (`POST /v1/calculations/upload-sessions`), after which the
 * requests name it by `stagedUploadId`.
 */
export const LOT_UPLOAD_MAX_BYTES = 25 * 1024 * 1024;
export const LOT_INLINE_UPLOAD_MAX_BYTES = 5 * 1024 * 1024;
const lotFileSourceShape = {
    contentBase64: z
        .string()
        .min(4)
        .max(7_000_000)
        // A grouped repeated pattern overflows the regex stack on a file over about 3.3 MB and
        // answered 500 instead of reading it; this check is linear (same helper as the NF-e upload).
        .refine(isCanonicalBase64, "contentBase64 must be canonical base64")
        .optional(),
    /** A file uploaded through an upload session; exactly one of this and contentBase64. */
    stagedUploadId: z.string().uuid().optional(),
};
/** Exactly one source of the file bytes: inline base64 or a staged upload. */
function oneFileSource(input, ctx) {
    if ((input.contentBase64 === undefined) === (input.stagedUploadId === undefined)) {
        ctx.addIssue({
            code: "custom",
            path: [input.contentBase64 === undefined ? "contentBase64" : "stagedUploadId"],
            message: "Send exactly one of contentBase64 and stagedUploadId",
        });
    }
}
const lotImportRequestObject = z
    .object({
    workbookId: z.string().uuid().optional(),
    /** Selects strict template validation at inspection, before mappings or fiscal work. */
    templateProfile: z.literal("JAMERSON_V1").optional(),
    mapping: lotMappingSchema.optional(),
    /**
     * The route of the whole file, declared by the person: FIXED or DYNAMIC
     * (each row states its own UF pair). Omitted means the legacy behaviour:
     * UF columns when mapped, otherwise `mapping.originUf` / `destinationUf`.
     */
    route: lotRouteSchema.optional(),
    /** Stored with the upload; the research worker reads it. Defaults to ASK. */
    researchMissing: researchMissingSchema.optional(),
    /**
     * Repairs the person confirmed in the "Reparar planilha" step (see lot-repair.ts). Each one names
     * a cell and the value that replaces it; the server checks every one against the cell and
     * refuses the import if one does not hold. Only valid together with a column mapping.
     */
    repairs: z.array(lotRepairSchema).max(LOT_MAX_REPAIRS).optional(),
    filename: z
        .string()
        .min(1)
        .max(200)
        // Case-insensitive on purpose (LISTA.XLSX is a common name). Written out as classes rather
        // than with the /i flag, which JSON Schema cannot carry: the published schema must say the same.
        .regex(/\.(?:[xX][lL][sS][xXmM]|[cC][sS][vV]|[tT][xX][tT])$/),
    ...lotFileSourceShape,
})
    .strict();
export const lotImportRequestSchema = lotImportRequestObject.superRefine(oneFileSource);
/**
 * POST /v1/calculations/repair-suggestions: the file, the mapping and the route as the person is
 * about to import them. Nothing is stored; the answer lists what the converter would block and what
 * could repair it.
 */
export const lotRepairSuggestionRequestSchema = lotImportRequestObject
    .omit({ workbookId: true, researchMissing: true, repairs: true, mapping: true })
    .extend({ mapping: lotMappingSchema })
    .strict()
    .superRefine(oneFileSource);
/**
 * POST /v1/calculations/upload-sessions: opens a one-object resumable upload to the private lot
 * bucket for a file too large to send inline. The browser PUTs the bytes to `uploadUrl` (Cloud
 * Storage, not the API) and then calls inspect, repair-suggestions and import with the
 * `stagedUploadId`. The object name is chosen by the server inside the caller's organization;
 * the size is fixed when the session opens and checked again when the API reads the file.
 */
export const lotUploadSessionRequestSchema = z
    .object({
    filename: lotImportRequestObject.shape.filename,
    sizeBytes: z.number().int().positive().max(LOT_UPLOAD_MAX_BYTES),
})
    .strict();
export const lotUploadSessionSchema = z
    .object({
    stagedUploadId: z.string().uuid(),
    /** Cloud Storage resumable session URI: a single-object upload capability, never logged. */
    uploadUrl: z.string().url().startsWith("https://storage.googleapis.com/"),
    method: z.literal("PUT"),
    sizeBytes: z.number().int().positive().max(LOT_UPLOAD_MAX_BYTES),
    /** After this the session can no longer be used and the staged object is deleted (1 day). */
    expiresAt: z.string().datetime(),
})
    .strict();
const expectedAmountSchema = z.string().regex(/^(0|[1-9]\d{0,15})(\.\d{1,2})?$/);
/**
 * One product fact as the file states it, with the cell it was read from ("C5"), so the screen and
 * the export can say where the value came from. Display only: never a calculation input.
 */
export const lotProductFactSchema = z
    .object({
    text: z.string().min(1).max(300),
    /** A1-style cell of the worksheet the row was read from. */
    cell: z.string().regex(/^[A-Z]{1,3}[1-9]\d{0,6}$/),
    /** The column header, as written in the file ("PESO CAIXA(Kg)"). */
    header: z.string().max(200).optional(),
})
    .strict();
/**
 * The product as the file describes it. It rides on the imported row, outside the calculation
 * item: it never enters the calculation request, its hash or a pinned result.
 */
export const lotProductInfoSchema = z
    .object({
    description: lotProductFactSchema.optional(),
    category: lotProductFactSchema.optional(),
    brand: lotProductFactSchema.optional(),
    family: lotProductFactSchema.optional(),
    ean: lotProductFactSchema.optional(),
    weight: lotProductFactSchema.optional(),
    warehouse: lotProductFactSchema.optional(),
    expiry: lotProductFactSchema.optional(),
})
    .strict();
export const lotImportResponseSchema = z
    .object({
    rows: z
        .array(z
        .object({
        /** 1-based worksheet row, as numbered in the file. */
        row: z.number().int().positive(),
        item: calculationItemInputSchema.nullable(),
        /** Blocking problems, one code per entry. Kept for older clients; see `issues`. */
        errors: z.array(z.string()),
        /** Structured findings for this row: blocking errors and warnings. */
        issues: z.array(workbookIssueSchema).max(30).optional(),
        /** Product description from the file; display only. */
        description: z.string().max(300).optional(),
        /** Product facts from the file with their cells (description, category, brand…). */
        product: lotProductInfoSchema.optional(),
        expectedTaxes: z
            .object({
            ipi: expectedAmountSchema.optional(),
            icmsSt: expectedAmountSchema.optional(),
            taxTotal: expectedAmountSchema.optional(),
            landed: expectedAmountSchema.optional(),
            /** Workbook column AA. */
            ownIcms: expectedAmountSchema.optional(),
            /** Workbook column AE. */
            purchasePisCofins: expectedAmountSchema.optional(),
            /** Workbook column AQ. */
            workbookCost: expectedAmountSchema.optional(),
            /** Workbook column AR. */
            salePrice: expectedAmountSchema.optional(),
            /** Workbook column AS. */
            saleIcms: expectedAmountSchema.optional(),
            /** Workbook column AT. */
            salePisCofins: expectedAmountSchema.optional(),
            /** Workbook column AU. */
            saleStFcp: expectedAmountSchema.optional(),
            /** Workbook column AV. */
            buyerInvoice: expectedAmountSchema.optional(),
        })
            .strict()
            .optional(),
        /** Rates the workbook states (CFOP, MVA, IPI, ICMS, PIS/COFINS, freight). Review only. */
        reference: z.record(z.string().max(30), z.string().max(60)).optional(),
        /**
         * Per-field cell provenance the mapping cannot carry (raw text, the sheet's own
         * inference). The sheet!cell of a mapped field is mapping.columns + row.
         */
        sources: z
            .object({
            origem_mercadoria: z
                .object({
                text: z.string().max(200),
                /** The file itself labels the value as inferred ("IMPORTADA (INFERIDA)"). */
                inferredBySheet: z.literal(true).optional(),
            })
                .strict()
                .optional(),
        })
            .strict()
            .optional(),
    })
        .strict())
        .max(LOT_MAX_DATA_ROWS),
    /** Findings that apply to the whole sheet (for example cached #REF! values). */
    issues: z.array(workbookIssueSchema).max(60).optional(),
})
    .strict();
/** Stored receipts retain display-only facts unknown to this release's converter. */
export const lotStoredProductInfoSchema = lotProductInfoSchema.extend({
    warehouse: lotProductFactSchema.optional(),
    expiry: lotProductFactSchema.optional(),
});
export const lotStoredImportResponseSchema = lotImportResponseSchema.extend({
    rows: z
        .array(lotImportResponseSchema.shape.rows.element.extend({
        product: lotStoredProductInfoSchema.optional(),
    }))
        .max(LOT_MAX_DATA_ROWS),
});
//# sourceMappingURL=lot-import.js.map